import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { QrCodeIcon, Check, Crown, Star, ArrowRight, MessageSquare, Bell } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentPlan = searchParams.get('current');
  const [billingCycle, setBillingCycle] = useState('yearly'); // 'monthly' or 'yearly'

  const handleSubscriptionUpgrade = async (planName) => {
    if (!user) {
      navigate('/auth');
      return;
    }

    if (planName === 'Free') {
      toast.info('You are already on the free plan');
      return;
    }

    try {
      await api.upgradeSubscription(planName.toLowerCase());
      toast.success(`Successfully upgraded to ${planName} plan!`);
      // Refresh the page or update user context to show new plan
      window.location.reload();
    } catch (error) {
      console.error('Subscription upgrade failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upgrade subscription. Please try again.');
    }
  };
  
  const plans = [
    {
      name: "Free",
      monthlyPrice: 0,
      yearlyPrice: 0,
      qrCodes: 10,
      messages: 10,
      sms: false,
      features: [
        "10 QR codes per month",
        "10 messages per month",
        "Email notifications only",
        "1 notification contact per QR",
        "Basic support",
        "Web dashboard access"
      ],
      highlighted: false,
      color: "gray"
    },
    {
      name: "Basic",
      monthlyPrice: 2,
      yearlyPrice: 20,
      savingsPercent: 17,
      qrCodes: 50,
      messages: 100,
      sms: true,
      features: [
        "50 QR codes per month",
        "100 messages per month",
        "Email & SMS notifications",
        "Up to 3 notification contacts per QR",
        "Priority email support",
        "Message history & analytics"
      ],
      highlighted: true,
      color: "blue"
    },
    {
      name: "Premium",
      monthlyPrice: 5,
      yearlyPrice: 49,
      savingsPercent: 18,
      qrCodes: 200,
      messages: 200,
      sms: true,
      features: [
        "200 QR codes per month",
        "200 messages per month",
        "Email & SMS notifications",
        "Up to 10 notification contacts per QR",
        "Premium support",
        "Advanced analytics dashboard",
        "Custom branding options"
      ],
      highlighted: false,
      color: "purple"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <QrCodeIcon className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">iComment.app</span>
            </Link>
            <nav className="flex items-center space-x-6">
              <Link to="/" className="text-gray-600 hover:text-gray-900">Home</Link>
              <Link to="/auth" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">Sign In</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Simple, Transparent
            <span className="block text-yellow-300">Pricing</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Choose the perfect plan for your communication needs. Upgrade or downgrade anytime.
          </p>
          
          {/* Billing Cycle Toggle */}
          <div className="flex items-center justify-center space-x-3">
            <span className={`text-lg font-medium ${billingCycle === 'monthly' ? 'text-white' : 'text-blue-200'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
              className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                billingCycle === 'yearly' ? 'bg-green-500' : 'bg-blue-400'
              }`}
              data-testid="billing-toggle"
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                  billingCycle === 'yearly' ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-lg font-medium ${billingCycle === 'yearly' ? 'text-white' : 'text-blue-200'}`}>
              Yearly
            </span>
            {billingCycle === 'yearly' && (
              <span className="ml-3 px-3 py-1 bg-green-500 text-white text-sm font-semibold rounded-full animate-pulse">
                Save up to 18%
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <div 
                key={index} 
                className={`bg-white rounded-xl shadow-lg overflow-hidden relative transition-all duration-300 hover:shadow-2xl ${
                  plan.highlighted ? 'ring-2 ring-blue-500 transform scale-105' : ''
                }`}
                data-testid={`pricing-card-${plan.name.toLowerCase()}`}
              >
                {plan.highlighted && (
                  <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white text-center py-2 px-4">
                    <Star className="inline h-4 w-4 mr-1" />
                    Most Popular
                  </div>
                )}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{plan.name}</h3>
                  
                  {/* Price Display */}
                  <div className="mb-6">
                    {plan.name === 'Free' ? (
                      <div>
                        <span className="text-4xl font-bold text-gray-900">Free</span>
                        <span className="text-gray-600 text-lg ml-2">forever</span>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-baseline">
                          <span className="text-5xl font-bold text-gray-900">
                            ${billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice}
                          </span>
                          <span className="text-gray-600 text-lg ml-2">
                            /{billingCycle === 'monthly' ? 'month' : 'year'}
                          </span>
                        </div>
                        {billingCycle === 'yearly' && (
                          <div className="mt-2 text-sm text-green-600 font-semibold">
                            Save ${(plan.monthlyPrice * 12) - plan.yearlyPrice} per year ({plan.savingsPercent}% off)
                          </div>
                        )}
                        {billingCycle === 'monthly' && (
                          <div className="mt-2 text-sm text-gray-500">
                            or ${plan.yearlyPrice}/year (save {plan.savingsPercent}%)
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Features List */}
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start">
                        <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {/* CTA Button */}
                  <button 
                    onClick={() => handleSubscriptionUpgrade(plan.name)}
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                      plan.highlighted
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 transform hover:scale-105'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                    data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                  >
                    {plan.name === 'Free' ? 'Get Started Free' : `Choose ${plan.name}`}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Compare Plans Link */}
        <div className="mt-12 text-center">
          <Link 
            to="/compare" 
            className="inline-flex items-center text-blue-600 hover:text-blue-800 font-semibold text-lg group"
          >
            Need more details? Compare all plans side-by-side
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Frequently Asked Questions</h2>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Can I change plans anytime?</h3>
              <p className="text-gray-600">Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">What happens if I exceed my limits?</h3>
              <p className="text-gray-600">We'll notify you when you're approaching your limits. You can upgrade your plan or wait for the next billing cycle.</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Is there a money-back guarantee?</h3>
              <p className="text-gray-600">Yes, we offer a 30-day money-back guarantee on all paid plans. No questions asked.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}